import React, { useEffect, useState, useRef, useMemo } from "react";
import { useTheme, Box, List, ListItem, Alert, Pagination, Button, Stack } from "@mui/material";
import Masonry from "@mui/lab/Masonry";
import { useTranslation } from "react-i18next";
import { defaultSettings } from "./EmbedSettings"
import WidgetLayout from "components/widgetParts/WidgetLayout";
import APIRequest from "system/APIRequest";
import { useSnackbar } from "notistack";
import useAuth from "system/APIAuth";
import Embed, { EmbedData } from "./Embed";
import withQueryWidget, { WithQueryWidgetProps } from "components/widgetParts/queryWidgetComposition/withQueryWidget";
import { getSingleWidgetResponseByName } from "system/widget/Widget";

function EmbedListContent(props: WithQueryWidgetProps): JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const auth = useAuth();

  const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'embed-list');

  const currentState = {...defaultSettings, ...props.widget?.data?.settings};
  const apiResponse = apiResponseInfo?.responseBody;
  const [cursor, setCursor] = useState<string | null>(apiResponse?.cursor || null);
  const [records, setRecords] = useState<any[]>([]);
  const [embedContainerWidth, setEmbedContainerWidth] = useState<number>(1);

  // Pagination and scrolling
  const [pageNumber, setPageNumber] = useState(1);
  const pageSize = currentState.pageSize; // Number of records per page
  const pageCount = Math.ceil(records.length / pageSize);
  const paginatedList = useMemo(() => {
    return records.slice((pageNumber - 1) * pageSize, pageNumber * pageSize);
  }, [records, pageNumber, pageSize]);
  const handlePageChange = (event: React.ChangeEvent<unknown> | null, value: number) => {
    setPageNumber(value);
    if (listRef.current) {
      listRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // Fetch more records
  const isAtEnd = auth.user && pageNumber === pageCount && !!cursor;
  const handleFetchMore = () => {
    const requestInfo = apiResponseInfo?.originatingRequest?.requestInit?.body || {};
    if (!requestInfo) {
      return;
    }

    const newRequestInfo = { ...requestInfo };

    newRequestInfo.records.size = requestInfo?.records?.size;
    newRequestInfo.records.cursor = cursor;

    APIRequest(`query/records/${apiResponseInfo?.originatingRequest?.activeQueryIds?.join(",") || ""}`,  {}, {
      method: "POST",
      body: JSON.stringify(newRequestInfo)
    }).then((response) => {
      if (response) {
        setRecords([...records, ...response.records]);
        setCursor(response.cursor || null);
        handlePageChange(null, pageNumber + 1);
      }
    }).catch((error) => {
      enqueueSnackbar(i18n.t("widget-types.embed-list.alerts.fetch-error", {errorMessage: error}), { variant: "error" });
    });
  }

  // Loads third-party embed scripts (Twitter, Instagram, TikTok, Reddit) for the current page.
  // Ensures each script is only added once and triggers embed (re)processing.
  // Cleans up only scripts added by this effect on unmount or page/record change.
  useEffect(() => {
    if (!records || records.length === 0) return;

    // Map each site to its script and post-process function
    // Post-process reloads the embeds during pagination
    const embedConfig: Record<string, { script: string, postProcess?: () => void }> = {
      "twitter.com": {
        script: "https://platform.twitter.com/widgets.js",
        postProcess: () => window.twttr?.widgets.load?.()
      },
      "instagram.com": {
        script: "//www.instagram.com/embed.js",
        postProcess: () => window.instgrm?.Embeds.process?.()
      },
      "tiktok.com": {
        script: "https://www.tiktok.com/embed.js",
        postProcess: () => {} // TikTok embeds auto-process on script load
      },
      "reddit.com": {
        script: "https://embed.reddit.com/widgets.js",
        postProcess: () => window.redditEmbed?.process?.()
      }
      // Youtube handled via iframe, no script needed
    };

    // Track which scripts we add so we can clean them up later
    const addedScripts: HTMLScriptElement[] = [];

    // Collect unique, normalized sites for this page of embeds
    const sites = Array.from(new Set(
      paginatedList
        .map((record: Record<string, string>) => (record.site || "").toLowerCase())
        .filter(site => embedConfig[site])
    ));

    // Load or Post-Process each site's embed script
    sites.forEach(site => {
      const { script, postProcess } = embedConfig[site];
      const existingScript = document.querySelector(`script[src="${script}"]`);
      // Only add the script if it doesn't already exist
      if (!existingScript) {
        const scriptEl = document.createElement("script");
        scriptEl.async = true;
        scriptEl.src = script;
        scriptEl.onload = () => postProcess?.();
        document.body.appendChild(scriptEl);
        addedScripts.push(scriptEl);
      } else { // Script already exists, just run post-process
        postProcess?.();
      }
    });

    // Cleanup only scripts we added
    return () => {
      addedScripts.forEach(script => script.remove());
    };
  }, [paginatedList, pageNumber]);

  // Update and append new records when apiResponse changes
  useEffect(() => {
    const apiResponseInfo = getSingleWidgetResponseByName(props.apiResponses, 'embed-list');
    setRecords([ ...records, ...(apiResponseInfo?.responseBody?.records || []) ]);
  }, [props.apiResponses]);

  const listRef = useRef<HTMLUListElement>(null);
  const embedListItem = {
    m: 0,
    p: .5,
    border: "1px solid transparent",
    borderRadius: "12px",
    backgroundColor: theme.palette.common.white,
    boxShadow: `0px 10px 16px 8px rgba(0,0,0, .02)`,
  }

  const embedRows = useMemo(() => {
    return paginatedList.map((embed: EmbedData) => {
      if (embed.link.includes("youtube.com")) {
        return null;
      } else {
        return (
          <ListItem key={embed.link} sx={embedListItem}>
            <Embed embed={embed} />
          </ListItem>
        );
      }
    });
  }, [paginatedList]);

  useEffect(() => {
    if (!listRef.current?.clientHeight) {
      return;
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setEmbedContainerWidth(entry.contentRect.width)
      }
    });

    observer.observe(listRef.current);

    return () => {
      observer.disconnect();
    }
  }, [records.length])

  // Twitter: 250px
  // Instagram: 326px
  // TikTok: 325px
  // Reddit: responsive
  const embedMinWidth = 326;
  const columnLimit = Math.max(Math.floor(embedContainerWidth / embedMinWidth), 1);
  const columnCount = Math.min(currentState.columns, columnLimit);

  return (
    <WidgetLayout
      footer={records.length > pageSize && (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", borderTop: `1px solid ${theme.palette.divider}`, p: 1 }}>
          <Pagination
            count={pageCount}
            page={pageNumber}
            shape="rounded"
            onChange={handlePageChange}
          />
          {isAtEnd &&
            <Button
              size="small"
              color="primary"
              variant="outlined"
              disabled={!isAtEnd}
              onClick={handleFetchMore}
            >
              {i18n.t("widget-types.embed-list.load-more")}
            </Button>
          }
        </Box>
      )}
    >
      {!records.length ? ( // If no records display alert
        <Stack sx={{ display: "flex", justifyContent: "center", m: 2 }}>
          <Alert severity="warning">
            {i18n.t("widget-types.embed-list.alerts.no-embeds")}
          </Alert>
          <Alert severity="info" sx={{ mt: 2 }}>
            {i18n.t("widget-types.embed-list.alerts.supported-sites")}
          </Alert>
        </Stack>
       ) : ( // Display embeds
        <List ref={listRef} sx={{ height: "100%", width: "100%", overflowY: "scroll", p: 1 }}>
          <Masonry
            columns={columnCount}
            spacing={currentState.spacing}
            sx={{ m: 0 }}
          >
            {embedRows}
          </Masonry>
        </List>
      )}
    </WidgetLayout>
  );
}

export default withQueryWidget(EmbedListContent, {
  hasSettingsDialog: true,
  hasQueryDialog: true,
  hideImageExport: true,
  exportOptions: ["50", "100", "250", "500", "1000", "2500", "5000", "10000"]
});
